import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ VEN-09 at v1.974 (venue slice 1, part C): a venue's standing instructions, and a job's copies
// of them. The database keeps the copies current; the screens only read them, and edit or remove one
// for a job alone. A job's are keyed under the job, so whatever refreshes the job refreshes them.

export type VenueInstruction = components['schemas']['VenueInstructionView'];
export type JobInstruction = components['schemas']['JobInstructionView'];

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const venueInstructionsQuery = (venueId: string) =>
  queryOptions({
    queryKey: ['venues', venueId, 'instructions'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/venues/{id}/instructions', {
        params: { path: { id: venueId } },
      });
      if (error) throw problem(error, 'Could not load the standing instructions.');
      return data;
    },
  });

export async function addInstruction(
  venueId: string,
  input: { text: string; roomId: string | null },
) {
  const { data, error } = await api.POST('/v1/venues/{id}/instructions', {
    params: { path: { id: venueId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not add that instruction.');
  return data;
}

export async function updateInstruction(
  venueId: string,
  instructionId: string,
  changes: { text?: string; roomId?: string | null },
) {
  const { data, error } = await api.PATCH('/v1/venues/{id}/instructions/{instructionId}', {
    params: { path: { id: venueId, instructionId } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not change that instruction.');
  return data;
}

export async function retireInstruction(venueId: string, instructionId: string) {
  const { error } = await api.DELETE('/v1/venues/{id}/instructions/{instructionId}', {
    params: { path: { id: venueId, instructionId } },
  });
  if (error) throw problem(error, 'Could not retire that instruction.');
}

export const jobInstructionsQuery = (jobId: string) =>
  queryOptions({
    queryKey: ['jobs', jobId, 'instructions'],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/jobs/{id}/instructions', {
        params: { path: { id: jobId } },
      });
      if (error) throw problem(error, 'Could not load the standing instructions.');
      return data;
    },
  });

export async function updateJobInstruction(
  jobId: string,
  copyId: string,
  changes: { text: string } | { reviewed: true },
) {
  const { data, error } = await api.PATCH('/v1/jobs/{id}/instructions/{copyId}', {
    params: { path: { id: jobId, copyId } },
    body: changes,
  });
  if (error) throw problem(error, 'Could not change that instruction.');
  return data;
}

/** VEN-09 at v1.975: an instruction for this job alone, for the whole job or one of its rooms. */
export async function addJobInstruction(
  jobId: string,
  input: { text: string; roomId: string | null },
) {
  const { data, error } = await api.POST('/v1/jobs/{id}/instructions', {
    params: { path: { id: jobId } },
    body: input,
  });
  if (error) throw problem(error, 'Could not add that instruction.');
  return data;
}

export async function removeJobInstruction(jobId: string, copyId: string) {
  const { error } = await api.DELETE('/v1/jobs/{id}/instructions/{copyId}', {
    params: { path: { id: jobId, copyId } },
  });
  if (error) throw problem(error, 'Could not remove that instruction.');
}
